import React, { useState, useMemo } from 'react';
import { View, Text, ScrollView } from 'react-native';
import { ChevronUp, ChevronDown } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import AnimatedPressable from '@/components/AnimatedPressable';

export interface TableColumn<T> {
  key: string;
  label: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  render?: (row: T) => React.ReactNode;
  getValue?: (row: T) => number | string;
  sortable?: boolean;
}

interface SortableTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  defaultSortKey?: string;
  defaultSortDir?: 'asc' | 'desc';
}

export function SortableTable<T>({
  columns,
  data,
  keyExtractor,
  defaultSortKey,
  defaultSortDir = 'desc',
}: SortableTableProps<T>) {
  const colors = useThemeColors();
  const [sortKey, setSortKey] = useState(defaultSortKey ?? columns[0]?.key ?? '');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSortDir);

  const sortedData = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.getValue) return data;

    return [...data].sort((a, b) => {
      const aVal = col.getValue!(a);
      const bVal = col.getValue!(b);
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal);
      const bStr = String(bVal);
      return sortDir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [data, sortKey, sortDir, columns]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
      <View>
        {/* Header */}
        <View
          className="flex-row items-center py-2.5"
          style={{ borderBottomWidth: 1, borderBottomColor: colors.border.light }}
        >
          {columns.map((col) => (
            <AnimatedPressable
              key={col.key}
              onPress={col.sortable !== false ? () => handleSort(col.key) : undefined}
              className="flex-row items-center gap-1 px-2"
              style={{ width: col.width ?? 80, justifyContent: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start' }}
            >
              <Text
                className="text-[10px] font-semibold uppercase"
                style={{ color: sortKey === col.key ? colors.primary[600] : colors.text.tertiary }}
                numberOfLines={1}
              >
                {col.label}
              </Text>
              {col.sortable !== false && sortKey === col.key && (
                sortDir === 'asc'
                  ? <ChevronUp size={10} color={colors.primary[600]} />
                  : <ChevronDown size={10} color={colors.primary[600]} />
              )}
            </AnimatedPressable>
          ))}
        </View>

        {/* Rows */}
        {sortedData.map((row) => (
          <View
            key={keyExtractor(row)}
            className="flex-row items-center py-2.5"
            style={{ borderBottomWidth: 1, borderBottomColor: colors.border.light }}
          >
            {columns.map((col) => (
              <View
                key={col.key}
                className="px-2"
                style={{ width: col.width ?? 80, alignItems: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start' }}
              >
                {col.render ? (
                  col.render(row)
                ) : (
                  <Text
                    className="text-xs"
                    style={{ color: colors.text.primary }}
                    numberOfLines={1}
                  >
                    {col.getValue ? String(col.getValue(row)) : ''}
                  </Text>
                )}
              </View>
            ))}
          </View>
        ))}

        {sortedData.length === 0 && (
          <View className="py-8 items-center">
            <Text style={{ color: colors.text.tertiary }} className="text-xs">No data</Text>
          </View>
        )}
      </View>
    </ScrollView>
  );
}

// Inline bar for use inside table cells
export function AdoptionBar({ value, color }: { value: number; color: string }) {
  const colors = useThemeColors();
  return (
    <View className="flex-row items-center gap-2 w-full">
      <View className="flex-1 h-4 rounded overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
        <View
          className="h-full rounded"
          style={{ width: `${Math.min(value, 100)}%`, backgroundColor: color }}
        />
      </View>
      <Text className="text-[10px] font-bold" style={{ color, width: 32, textAlign: 'right' }}>
        {value}%
      </Text>
    </View>
  );
}

// Trend indicator for use inside table cells
export function TrendBadge({ value }: { value: number }) {
  const isPositive = value >= 0;
  const color = isPositive ? '#22C55E' : '#EF4444';
  return (
    <Text className="text-[10px] font-bold" style={{ color }}>
      {isPositive ? '+' : ''}{value.toFixed(1)}%
    </Text>
  );
}
